import type { ISchoolClassroomRepository } from '@/core/domain/school/classroom/ISchoolClassroomRepository'
import SchoolClassroom, {
  SchoolClassroomListItem,
  SchoolClassroomInput
} from '@/core/domain/school/classroom/SchoolClassroom'
import { SchoolClassroomLoginHistory } from '@/core/domain/school/classroom/SchoolClassroomLoginHistory'
import { apiService } from '@/core/infrastructure/services/apiServiceFactory'
import type {
  SchoolClassroomsListItemEntity
} from '@/core/infrastructure/schemas/school/classroom/SchoolClassroomSchema.ts'

export default class SchoolClassroomRepository implements ISchoolClassroomRepository {
  /**
   * Récupère une classe pour une période scolaire donnée
   * @param schoolClassroomId Identifiant de la classe
   * @param schoolId Identifiant de l'école
   * @param schoolPeriodId Identifiant de la période scolaire
   * @returns Classe de l'école
   */
  async fetchSchoolClassroom(
    schoolId: number,
    schoolPeriodId: number,
    schoolClassroomId: number
  ): Promise<SchoolClassroom> {
    const response = await apiService.get(
      `admin/schools/${schoolId}/periods/${schoolPeriodId}/classrooms/${schoolClassroomId}`
    )
    return SchoolClassroom.create(response.data)
  }

  /**
   * Récupère l'ensemble des classes d'une école pour une période scolaire donnée
   * @param schoolId Identifiant de l'école
   * @param schoolPeriodId Identifiant de la période scolaire
   * @returns Liste des classes de l'école
   */
  async fetchAllSchoolClassrooms(
    schoolId: number,
    schoolPeriodId: number
  ): Promise<SchoolClassroomListItem[]> {
    const response = await apiService.get(
      `admin/schools/${schoolId}/periods/${schoolPeriodId}/classrooms`
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for school classrooms')
    }

    return response.data.map((classroom) => SchoolClassroomListItem.create(classroom))
  }

  async createSchoolClassroom(
    schoolId: number,
    schoolClassroomInput: SchoolClassroomInput
  ): Promise<SchoolClassroomListItem> {
    const response = await apiService.post(
      `admin/schools/${schoolId}/periods/${schoolClassroomInput.schoolPeriodId}/classrooms`,
      schoolClassroomInput.classroomInput
    )
    return SchoolClassroomListItem.create(response.data)
  }

  /**
   * Met à jour une classe
   * @param schoolId Identifiant de l'école
   * @param schoolPeriod Identifiant de la période scolaire
   * @param schoolClassroom Identifiant de la classe
   * @returns Classe mise à jour
   */
  async updateSchoolClassroom(
    schoolId: number,
    schoolPeriod: number,
    schoolClassroom: SchoolClassroomInput
  ): Promise<SchoolClassroomListItem> {
    const response = await apiService.put<SchoolClassroomsListItemEntity>(
      `admin/schools/${schoolId}/periods/${schoolPeriod}/classrooms/${schoolClassroom.schoolClassroomId}`,
      schoolClassroom.classroomInput
    )

    return SchoolClassroomListItem.create(response.data)
  }

  /**
   * Met à jour les utilisateurs d'une classe
   * @param schoolId Identifiant de l'école
   * @param periodId Identifiant de la période scolaire
   * @param schoolClassroomId Identifiant de la classe
   * @param users Identifiant des utilisateurs
   * @returns Classe mise à jour
   */
  async updateSchoolClassroomUsers(
    schoolId: number,
    periodId: number,
    schoolClassroomId: number,
    users: number[]
  ): Promise<SchoolClassroomListItem> {
    const response = await apiService.put(
      `admin/schools/${schoolId}/periods/${periodId}/classrooms/${schoolClassroomId}/users`,
      { classroomId: schoolClassroomId, users }
    )
    return SchoolClassroomListItem.create(response.data)
  }

  /**
   * Supprime une classe
   * @param schoolId Identifiant de l'école
   * @param schoolPeriodId Identifiant de la période scolaire
   * @param schoolClassroomId Identifiant de la classe
   */
  async deleteSchoolClassroom(
    schoolId: number,
    schoolPeriodId: number,
    schoolClassroomId: number
  ): Promise<void> {
    await apiService.delete(
      `admin/schools/${schoolId}/periods/${schoolPeriodId}/classrooms/${schoolClassroomId}`
    )
  }

  /**
   * Récupère l'historique de connexion d'une classe pour une période scolaire donnée
   * @param schoolClassroomId Identifiant de la classe
   * @param schoolId Identifiant de l'école
   * @param schoolPeriodId Identifiant de la période scolaire
   * @returns Historique de connexion des élèves de la classe
   */
  async fetchSchoolClassroomLoginHistory(
    schoolId: number,
    schoolPeriodId: number,
    schoolClassroomId: number
  ): Promise<SchoolClassroomLoginHistory> {
    const response = await apiService.get<SchoolClassroomLoginHistory>(
      `admin/schools/${schoolId}/periods/${schoolPeriodId}/classrooms/${schoolClassroomId}/login-history`
    )
    return SchoolClassroomLoginHistory.create(response.data)
  }
}
